import { defineStore } from "pinia";
import { ref, computed } from "vue";
import HTTPService from "@/core/services/HTTPService";
import JwtService from "@/core/services/JwtService";
import ApiService from "@/core/services/ApiService";
import { IUser } from "@/contracts/User";
export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<IUser>({} as IUser);
  const isAuthenticated = ref<boolean>(!!JwtService.getToken());
  const errors = ref<Record<string, any>>({});
  const loading = ref<boolean>(false);

  // Getters
  const currentUser = computed(() => user.value);
  const isUserAuthenticated = computed(() => isAuthenticated.value);
  const getErrors = computed(() => errors.value);

  // Actions
  /**
   * Вход в систему
   * POST /login
   * @param credentials - Данные для входа (email, password)
   */
  async function login(credentials: { email: string; password: string }) {
    loading.value = true;
    errors.value = {};

    try {
      const response = await HTTPService.post("login", credentials);
      if (HTTPService.isValid(response)) {
        const data = response.data;
        const status = response.status;
        const isErrorResponse = status === 401 || data?.status === "error";
        if (isErrorResponse && data?.message) {
          errors.value = { login: data.message };
          const err = new Error(data.message) as any;
          err.response = response;
          throw err;
        }
        const userPayload = data.data || data;
        const userData =
          data.authorization != null
            ? { ...userPayload, authorization: data.authorization }
            : userPayload;
        setAuth(userData);
        return userData;
      }
    } catch (err: any) {
      const res = err?.response;
      if (res?.data) {
        const msg = res.data.message;
        const errs = res.data.errors;
        if (errs && typeof errs === "object" && Object.keys(errs).length > 0) {
          errors.value = errs;
        } else if (msg) {
          errors.value = { login: msg };
        } else {
          errors.value = { login: "Ошибка авторизации" };
        }
      } else {
        errors.value = HTTPService.serverError();
      }
      console.error("Error during login:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Регистрация нового пользователя
   * POST /register
   * @param credentials - Данные для регистрации
   */
  async function register(credentials: Record<string, any>) {
    loading.value = true;
    errors.value = {};

    try {
      const response = await HTTPService.post("register", credentials);
      if (HTTPService.isValid(response)) {
        const userData = response.data.data || response.data;
        setAuth(userData);
        return userData;
      }
    } catch (err: any) {
      errors.value = HTTPService.isValid(err?.response)
        ? err.response.data.errors
        : HTTPService.serverError();
      console.error("Error during registration:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Получить информацию о текущем пользователе
   * POST /me
   */
  async function me() {
    loading.value = true;
    errors.value = {};

    try {
      const response = await HTTPService.post("me", {});
      if (HTTPService.isValid(response)) {
        const userData = response.data.data || response.data;

        if (
          typeof userData === "undefined" ||
          typeof userData.id === "undefined"
        ) {
          errors.value = HTTPService.serverError();
          purgeAuth();
        } else {
          setAuth(userData);
        }
        return userData;
      }
    } catch (err: any) {
      errors.value = HTTPService.isValid(err?.response)
        ? err.response.data.errors
        : HTTPService.serverError();
      console.error("Error fetching user data:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Выход из системы
   */
  function logout() {
    purgeAuth();
  }

  /**
   * Установить данные авторизации
   * @param userData - Данные пользователя
   */
  function setAuth(userData: IUser) {
    isAuthenticated.value = true;
    user.value = userData;
    errors.value = {};

    if (userData.authorization?.token) {
      JwtService.saveToken(userData.authorization.token);
      ApiService.createRequestHandler();
    }
  }

  /**
   * Обновить данные пользователя
   * @param userData - Новые данные пользователя
   */
  function setUser(userData: Partial<IUser>) {
    user.value = { ...user.value, ...userData };
  }

  /**
   * Установить пароль пользователя
   * @param password - Новый пароль
   */
  function setPassword(password: string) {
    user.value.password = password;
  }

  /**
   * Очистить авторизацию
   */
  function purgeAuth() {
    isAuthenticated.value = false;
    user.value = {} as IUser;
    errors.value = {};
    JwtService.destroyToken();
  }

  /**
   * Очистить ошибки
   */
  function clearError() {
    errors.value = {};
  }

  /**
   * Восстановление пароля
   * @param payload - Данные для восстановления пароля
   */
  async function forgotPassword(payload: Record<string, any>) {
    loading.value = true;
    errors.value = {};

    try {
      const response = await HTTPService.post("forgot-password", payload);
      if (HTTPService.isValid(response)) {
        return response.data;
      }
    } catch (err: any) {
      errors.value = HTTPService.isValid(err?.response)
        ? err.response.data.errors
        : HTTPService.serverError();
      console.error("Error during password recovery:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Проверка авторизации
   * @param payload - Данные для проверки
   */
  async function verifyAuth(payload?: Record<string, any>) {
    // Реализация проверки авторизации
    // Можно использовать для проверки токена и обновления данных пользователя
    if (JwtService.getToken()) {
      await me();
    }
  }

  return {
    // State
    user,
    isAuthenticated,
    errors,
    loading,
    // Getters
    currentUser,
    isUserAuthenticated,
    getErrors,
    // Actions
    login,
    register,
    me,
    logout,
    setAuth,
    setUser,
    setPassword,
    purgeAuth,
    clearError,
    forgotPassword,
    verifyAuth,
  };
});
